/*
 * Copyright (c) 2024-2025. Haodong JU
 */

import EntitySelector from './EntitySelector.vue'
import Dish from '@/model/dataLayer/api/dish/Dish'

/**
 * Creates a schema for an EntitySelector component to be used in BaseForm
 *
 * @param {Object} options - Configuration options for the EntitySelector
 * @param {string} options.key - The property key in the form data
 * @param {string} options.name - Display name for the field
 * @param {Function} options.loadFunction - Function to load the list of entities
 * @param {Function} options.addFunction - Function to add a new entity
 * @param {Array} options.formSchemas - Schema for the add entity form
 * @param {string} [options.hint=''] - Help text to display
 * @param {string} [options.itemText='name'] - Property name to display in the dropdown
 * @param {string} [options.itemValue='id'] - Property name to use as the value
 * @param {string} [options.label='Select an entity'] - Label for the selector
 * @param {string} [options.dialogTitle='Add New Entity'] - Title for the add dialog
 * @param {string} [options.noDataText='No data available'] - Text to show when no data is available
 * @param {string} [options.saveBtnName='save'] - Text for the save button
 * @param {boolean} [options.required=true] - Whether the field is required
 * @param {boolean} [options.clearable=true] - Whether the field can be cleared
 * @param {boolean} [options.selectionOnly=false] - Whether to only allow selection (no add button)
 * @param {Function} [options.shouldShow=() => true] - Function to determine if the field should be shown
 * @param {Object} [options.preconditions=null] - Preconditions for showing the field
 * @returns {Object} - Schema object for BaseForm
 */
export function createEntitySelectorSchema (options) {
  const {
    key,
    name,
    loadFunction,
    addFunction,
    formSchemas,
    hint = '',
    itemText = 'name',
    itemValue = 'id',
    label = 'Select an entity',
    dialogTitle = 'Add New Entity',
    noDataText = 'No data available',
    saveBtnName = 'save',
    required = true,
    clearable = true,
    selectionOnly = false,
    shouldShow = () => true,
    preconditions = null
  } = options

  return {
    key,
    name,
    component: EntitySelector,
    required,
    default: null,
    hint,
    shouldShow,
    preconditions,
    componentProps: {
      loadFunction,
      addFunction,
      formSchemas,
      itemText,
      itemValue,
      label,
      dialogTitle,
      noDataText,
      saveBtnName,
      clearable,
      selectionOnly,
      rules: required ? [v => !!v || 'This field is required'] : []
    }
  }
}

/**
 * Creates a schema for a Product/Dish EntitySelector component
 * This is a convenience function that pre-configures createEntitySelectorSchema for products
 *
 * @param {Object} options - Configuration options for the ProductSelector
 * @param {string} options.key - The property key in the form data
 * @param {string} [options.name='产品'] - Display name for the field
 * @param {string} [options.hint='选择产品'] - Help text to display
 * @param {boolean} [options.required=false] - Whether the field is required
 * @param {boolean} [options.multiple=false] - Whether multiple selection is allowed
 * @param {boolean} [options.selectionOnly=true] - Whether to only allow selection (no add button)
 * @param {string} [options.tag] - Tag for grouping fields
 * @param {Object} [options.preconditions=null] - Preconditions for showing the field
 * @returns {Object} - Schema object for BaseForm
 */
export function createProductSelectorSchema (options) {
  const {
    key,
    name = '产品',
    hint = '选择产品',
    required = false,
    multiple = false,
    selectionOnly = true,
    tag = null,
    preconditions = null
  } = options

  return createEntitySelectorSchema({
    key,
    name,
    hint,
    required,
    loadFunction: async () => await Dish.getList(true),
    addFunction: async () => {}, // Not used in selection-only mode
    formSchemas: [], // Not used in selection-only mode
    itemText: 'dishName',
    itemValue: 'code',
    selectionOnly,
    multiple,
    tag,
    preconditions
  })
}

/**
 * Example usage:
 *
 * import { createEntitySelectorSchema } from '@/views/AadenPages/components/EntitySelector/entitySelectorUtils'
 *
 * const schema = [
 *   createEntitySelectorSchema({
 *     key: 'category',
 *     name: 'Category',
 *     loadFunction: async () => {
 *       // Load categories from API
 *       return categoriesList
 *     },
 *     addFunction: async (categoryData) => {
 *       // Add new category via API
 *       return newCategory
 *     },
 *     formSchemas: [
 *       // Schema for the add category form
 *     ],
 *     hint: 'Select or create a category'
 *   })
 * ]
 */
